/**
 * Share Command Center Modal
 *
 * Modal for sharing command center room with role selection (Commander/Deputy).
 * Deputies have coordinator-level access to help manage the command center.
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { createSecureShareData } from '@/lib/crypto/room-encryption'

interface ShareCommandCenterProps {
  roomId: string
  roomSecret: string
  roomName: string
  pin?: string
  onClose: () => void
  baseUrl?: string
}

type ShareRole = 'deputy' | 'participant'

export function ShareCommandCenter({
  roomId,
  roomSecret,
  roomName,
  pin,
  onClose,
  baseUrl: _baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://crowdtalkie.com',
}: ShareCommandCenterProps) {
  void _baseUrl // May be used later for custom base URL support
  const [selectedRole, setSelectedRole] = useState<ShareRole>('deputy')
  const [linkCopied, setLinkCopied] = useState(false)
  const [secretCopied, setSecretCopied] = useState(false)
  const [pinCopied, setPinCopied] = useState(false)
  const [showQR, setShowQR] = useState(true)

  // Generate share data - role is embedded in URL for automatic assignment
  const shareData = useMemo(() => {
    return createSecureShareData(roomId, roomSecret, roomName, pin)
  }, [roomId, roomSecret, roomName, pin])

  // Generate role-specific join URL
  const roleUrl = useMemo(() => {
    const url = new URL(shareData.joinUrl)
    url.searchParams.set('role', selectedRole)
    url.searchParams.set('hierarchyType', 'command')
    return url.toString()
  }, [shareData.joinUrl, selectedRole])

  // QR data with role
  const qrData = useMemo(() => {
    try {
      const parsed = JSON.parse(shareData.qrData)
      return JSON.stringify({
        ...parsed,
        role: selectedRole,
        hierarchyType: 'command',
      })
    } catch {
      return shareData.qrData
    }
  }, [shareData.qrData, selectedRole])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(roleUrl)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [roleUrl])

  const handleCopySecret = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareData.roomSecret)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [shareData.roomSecret])

  const handleCopyPin = useCallback(async () => {
    if (!pin) return
    try {
      await navigator.clipboard.writeText(pin)
      setPinCopied(true)
      setTimeout(() => setPinCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy PIN:', err)
    }
  }, [pin])

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <div
      className="bg-gradient-to-b from-[#1a1028] to-[#0d0a10] border border-purple-500/30 rounded-2xl max-w-md w-full shadow-[0_0_60px_rgba(139,92,246,0.2)] max-h-[90vh] flex flex-col overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header - Command Console Style */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-900/40 via-transparent to-purple-900/40 border-b border-purple-500/30 flex-shrink-0">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2 tracking-wide">
            <span className="text-xl">👑</span> Share Command Center
          </h2>
          <p className="text-sm text-purple-300 mt-0.5 font-medium font-mono">{roomName}</p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all border border-white/10 hover:border-white/30"
        >
          ✕
        </button>
      </div>

        {/* Scrollable Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1 scrollbar-hide">
          {/* Role Selection - Enhanced */}
          <div className="space-y-3">
            <label className="text-[10px] text-purple-300/80 uppercase tracking-widest font-bold flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-purple-400" />
              Select Role
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setSelectedRole('deputy')}
                className={`p-4 rounded-xl border-2 transition-all text-left relative overflow-hidden ${
                  selectedRole === 'deputy'
                    ? 'bg-gradient-to-br from-purple-500/30 to-purple-600/20 border-purple-400/60 shadow-[0_0_25px_rgba(139,92,246,0.3)]'
                    : 'bg-black/30 border-white/10 hover:bg-white/5 hover:border-white/20'
                }`}
              >
                {selectedRole === 'deputy' && (
                  <div className="absolute top-0 right-0 w-16 h-16 bg-purple-400/20 rounded-full blur-xl" />
                )}
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🎖️</span>
                    <span className={`text-sm font-bold uppercase tracking-wider ${selectedRole === 'deputy' ? 'text-purple-300' : 'text-white/80'}`}>
                      Deputy
                    </span>
                  </div>
                  <p className="text-[10px] text-white/50 leading-relaxed">
                    Full command access - broadcasts, escalations, sector management
                  </p>
                  {selectedRole === 'deputy' && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-400 rounded-full flex items-center justify-center text-[10px] text-white">
                      ✓
                    </div>
                  )}
                </div>
              </button>
              <button
                onClick={() => setSelectedRole('participant')}
                className={`p-4 rounded-xl border-2 transition-all text-left relative overflow-hidden ${
                  selectedRole === 'participant'
                    ? 'bg-gradient-to-br from-blue-500/30 to-blue-600/20 border-blue-400/60 shadow-[0_0_25px_rgba(59,130,246,0.3)]'
                    : 'bg-black/30 border-white/10 hover:bg-white/5 hover:border-white/20'
                }`}
              >
                {selectedRole === 'participant' && (
                  <div className="absolute top-0 right-0 w-16 h-16 bg-blue-400/20 rounded-full blur-xl" />
                )}
                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">👁️</span>
                    <span className={`text-sm font-bold uppercase tracking-wider ${selectedRole === 'participant' ? 'text-blue-300' : 'text-white/80'}`}>
                      Observer
                    </span>
                  </div>
                  <p className="text-[10px] text-white/50 leading-relaxed">
                    View-only access - monitor dashboard, no changes
                  </p>
                  {selectedRole === 'participant' && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-400 rounded-full flex items-center justify-center text-[10px] text-white">
                      ✓
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* QR Code */}
          {showQR && (
            <div className="flex flex-col items-center py-4">
              <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG
                  id="command-center-qr"
                  value={qrData}
                  size={160}
                  level="M"
                  includeMargin={false}
                  fgColor="#181111"
                  bgColor="#ffffff"
                />
              </div>
              <p className="text-xs text-white/40 mt-2">
                Scan to join as {selectedRole === 'deputy' ? 'Deputy Commander' : 'Observer'}
              </p>
            </div>
          )}

          {/* Toggle QR */}
          <button
            onClick={() => setShowQR(!showQR)}
            className="w-full text-xs text-white/60 hover:text-white py-1 transition-colors"
          >
            {showQR ? 'Hide QR Code' : 'Show QR Code'}
          </button>

          {/* Share Link */}
          <div className="space-y-2">
            <label className="text-xs text-white/60 uppercase tracking-wider font-bold">
              Share Link ({selectedRole === 'deputy' ? 'Deputy' : 'Observer'})
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={roleUrl}
                readOnly
                className="flex-1 bg-secondary/50 text-white/80 text-xs rounded-lg p-2
                  border border-white/10 focus:outline-none font-mono"
              />
              <button
                onClick={handleCopyLink}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${linkCopied
                    ? 'bg-green-500 text-white'
                    : 'bg-primary text-white hover:bg-primary/90'
                  }`}
              >
                {linkCopied ? '✓' : '📋'}
              </button>
            </div>
          </div>

          {/* Room Secret */}
          <div className="space-y-2">
            <label className="text-xs text-yellow-400 uppercase tracking-wider font-bold">
              Room Secret (share separately)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareData.roomSecret.substring(0, 12) + '...'}
                readOnly
                className="flex-1 bg-yellow-500/10 text-yellow-200/80 text-xs rounded-lg p-2
                  border border-yellow-500/30 focus:outline-none font-mono"
              />
              <button
                onClick={handleCopySecret}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${secretCopied
                    ? 'bg-green-500 text-white'
                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30'
                  }`}
              >
                {secretCopied ? '✓' : '🔐'}
              </button>
            </div>
            <p className="text-[10px] text-yellow-400/60 flex items-center gap-1">
              <span>⚠</span> Send via secure channel (Signal, in-person, etc.)
            </p>
          </div>

          {/* PIN if set */}
          {pin && (
            <div className="space-y-2">
              <label className="text-xs text-red-400 uppercase tracking-wider font-bold">
                Access PIN
              </label>
              <div className="flex gap-2 items-center">
                <div className="flex-1 bg-red-500/10 border border-red-500/30 rounded-lg p-2 flex items-center justify-between">
                  <span className="font-mono text-lg font-bold tracking-[0.2em] text-red-200">
                    {pin}
                  </span>
                  <button
                    onClick={handleCopyPin}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors
                      ${pinCopied
                        ? 'bg-green-500 text-white'
                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                      }`}
                  >
                    {pinCopied ? '✓' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Role Description */}
          <div className="bg-white/5 rounded-lg p-3 border border-white/10">
            <p className="text-xs text-white/80 font-medium mb-2">
              {selectedRole === 'deputy' ? '🎖️ Deputy Commander can:' : '👤 Observer can:'}
            </p>
            <ul className="text-[10px] text-white/60 space-y-1">
              {selectedRole === 'deputy' ? (
                <>
                  <li>• View all sectors and their status</li>
                  <li>• Send broadcasts to sector rooms</li>
                  <li>• Acknowledge escalations</li>
                  <li>• Create and manage sectors</li>
                  <li>• Assign organizers to sectors</li>
                </>
              ) : (
                <>
                  <li>• View command dashboard</li>
                  <li>• See sector statuses</li>
                  <li>• Monitor broadcasts and escalations</li>
                  <li>• Cannot make changes or send commands</li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-purple-500/20 flex-shrink-0 bg-black/30">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-xl font-bold uppercase tracking-wider
              hover:from-purple-500 hover:to-purple-400 transition-all text-sm shadow-[0_0_20px_rgba(139,92,246,0.3)]"
          >
            Done
          </button>
        </div>
      </div>
  )
}

export default ShareCommandCenter
